import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {NavigationEnd, NavigationStart, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {isPlatformBrowser} from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class ScrollPositionService {
  private readonly scrollPositions = new Map<string, number>();
  private currentUrl: string = '';

  constructor(
    private readonly router: Router,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    this.initializeScrollTracking();
  }

  restoreScrollForUrl(url: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const savedPosition = this.getPosition(url);

    if (savedPosition !== null && savedPosition > 0) {
      // Only restore if there's a saved position greater than 0
      requestAnimationFrame(() => {
        this.performScroll(savedPosition);

        // Additional attempts for stubborn cases
        setTimeout(() => this.performScroll(savedPosition), 50);
        setTimeout(() => this.performScroll(savedPosition), 150);
      });
    } else {
      // Scroll to top for new visits or if saved position is 0
      requestAnimationFrame(() => this.performScroll(0));
    }
  }

  savePosition(url: string, position: number): void {
    if (isPlatformBrowser(this.platformId)) {
      this.scrollPositions.set(url, position);
    }
  }

  getPosition(url: string): number | null {
    return this.scrollPositions.get(url) || null;
  }

  getCurrentScrollPosition(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return 0;
    }
    return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
  }

  clearPosition(url: string): void {
    this.scrollPositions.delete(url);
  }

  clearAll(): void {
    this.scrollPositions.clear();
  }

  private initializeScrollTracking(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.router.events.pipe(
      filter((event): event is NavigationStart => event instanceof NavigationStart)
    ).subscribe(() => {
      if (this.currentUrl) {
        const position = this.getCurrentScrollPosition();
        this.savePosition(this.currentUrl, position);
      }
    });


    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {
      this.currentUrl = event.urlAfterRedirects;
    });
  }

  private performScroll(position: number): void {
    if (!isPlatformBrowser(this.platformId)) return;

    window.scrollTo({
      top: position,
      behavior: 'auto'
    });
  }
}
